import { useCallback, useEffect, useState } from "react";
import { api, formatApiError, formatCurrency } from "@/lib/api";
import { MoneyInput } from "@/components/MoneyInput";
import { Input } from "@/components/ui/input";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";

/**
 * Override provvigione per singola garanzia sul link Partner↔Compagnia.
 * Per ogni garanzia della compagnia collegata, l'utente può impostare:
 *  - % provvigione specifica
 *  - Costo fisso (in €)
 * Se entrambi vuoti → si usa la config di default del link (branch_rates).
 */
export function PartnerLinkGuaranteesSubPanel({ linkId, canWrite = true }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [dirty, setDirty] = useState({}); // { guarantee_id: { commission_percent, fixed_cost, notes } }

  const load = useCallback(async () => {
    if (!linkId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/partner-company-links/${linkId}/guarantees`);
      setItems(Array.isArray(data) ? data : []);
      setDirty({});
    } catch (e) { toast.error(formatApiError(e)); setItems([]); }
    finally { setLoading(false); }
  }, [linkId]);
  useEffect(() => { load(); }, [load]);

  const setField = (gid, field, val) => {
    setDirty((d) => ({ ...d, [gid]: { ...(d[gid] || {}), [field]: val } }));
  };

  const getVal = (row, field) => {
    if (dirty[row.guarantee_id] && field in dirty[row.guarantee_id]) return dirty[row.guarantee_id][field];
    return row[field] ?? "";
  };

  const parseNumOrNull = (v) => (v === "" || v == null) ? null : Number(v);

  const saveRow = async (row) => {
    const gid = row.guarantee_id;
    const payload = {
      commission_percent: parseNumOrNull(getVal(row, "commission_percent")),
      fixed_cost: parseNumOrNull(getVal(row, "fixed_cost")),
      notes: (getVal(row, "notes") || "").toString().trim() || null,
    };
    setSavingId(gid);
    try {
      await api.put(`/partner-company-links/${linkId}/guarantees/${gid}`, payload);
      toast.success("Salvato");
      // Aggiorna riga in memoria
      setItems((prev) => prev.map((x) => x.guarantee_id === gid ? { ...x, ...payload } : x));
      setDirty((d) => { const n = { ...d }; delete n[gid]; return n; });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingId(null); }
  };

  return (
    <div className="mt-2 border-t border-slate-200 pt-2" data-testid={`link-guarantees-panel-${linkId}`}>
      <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 mb-2">
        🛡️ Provvigioni per garanzia
      </div>
      {loading ? (
        <div className="text-xs text-slate-400"><Loader2 className="h-3 w-3 inline animate-spin mr-1" /> Caricamento…</div>
      ) : items.length === 0 ? (
        <div className="text-xs text-slate-500 italic">La compagnia collegata non ha garanzie configurate. Aggiungile da <strong>Compagnie → Garanzie</strong>.</div>
      ) : (
        <div className="bg-slate-50 border border-slate-200 rounded overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-slate-100 text-[10px] uppercase text-slate-600">
              <tr>
                <th className="text-left px-2 py-1.5 w-[40px]">Ord.</th>
                <th className="text-left px-2 py-1.5">Garanzia</th>
                <th className="text-right px-2 py-1.5 w-[110px]">% Provv.</th>
                <th className="text-right px-2 py-1.5 w-[110px]">Costo fisso €</th>
                <th className="text-left px-2 py-1.5 w-[140px]">Note</th>
                {canWrite && <th className="text-right px-2 py-1.5 w-[70px]"></th>}
              </tr>
            </thead>
            <tbody>
              {items.map((row) => {
                const isDirty = !!dirty[row.guarantee_id];
                return (
                  <tr key={row.guarantee_id} className={`border-t border-slate-100 ${isDirty ? "bg-blue-50/40" : ""}`} data-testid={`link-guarantee-row-${row.guarantee_id}`}>
                    <td className="px-2 py-1 text-slate-500 tabular-nums text-[10px]">{row.sort_index != null ? row.sort_index : "—"}</td>
                    <td className="px-2 py-1 font-medium text-slate-800">
                      {row.guarantee_name}
                      <div className="text-[10px] text-slate-500 flex gap-2 mt-0.5">
                        {row.imposte_percent != null && <span>Imp.: {row.imposte_percent}%</span>}
                        {row.ssn_percent != null && <span>SSN: {row.ssn_percent}%</span>}
                        {row.sell_price != null && <span>Ven.: {formatCurrency(row.sell_price)}</span>}
                      </div>
                    </td>
                    <td className="px-2 py-1 text-right">
                      <Input
                        data-testid={`link-guarantee-pct-${row.guarantee_id}`}
                        type="number" step="0.01" min="0" max="100"
                        className="h-7 text-xs text-right"
                        value={getVal(row, "commission_percent")}
                        disabled={!canWrite}
                        onChange={(e) => setField(row.guarantee_id, "commission_percent", e.target.value)}
                        placeholder="—"
                      />
                    </td>
                    <td className="px-2 py-1 text-right">
                      <MoneyInput
                        data-testid={`link-guarantee-fixed-${row.guarantee_id}`}
                        className="h-7 text-xs"
                        value={getVal(row, "fixed_cost") ?? ""}
                        onChange={(v) => setField(row.guarantee_id, "fixed_cost", v)}
                        placeholder="0,00"
                      />
                    </td>
                    <td className="px-2 py-1">
                      <Input
                        data-testid={`link-guarantee-notes-${row.guarantee_id}`}
                        className="h-7 text-xs"
                        value={getVal(row, "notes") || ""}
                        disabled={!canWrite}
                        onChange={(e) => setField(row.guarantee_id, "notes", e.target.value)}
                        placeholder="Note"
                      />
                    </td>
                    {canWrite && (
                      <td className="px-2 py-1 text-right">
                        {isDirty && (
                          <button
                            data-testid={`link-guarantee-save-${row.guarantee_id}`}
                            className="inline-flex items-center gap-1 text-[11px] bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded"
                            disabled={savingId === row.guarantee_id}
                            onClick={() => saveRow(row)}
                          >
                            {savingId === row.guarantee_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                            Salva
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-[10px] text-slate-500 mt-1">Se sia % che costo fisso sono vuoti, il calcolo provvigioni usa la configurazione di default del collegamento (branch rates).</p>
    </div>
  );
}

export default PartnerLinkGuaranteesSubPanel;
